import {
    ApolloError,
    FetchResult,
    gql,
    MutationTuple,
    QueryResult,
    StoreObject,
    useApolloClient,
    useMutation,
    useQuery,
} from '@apollo/client'
import {useEffect, useState} from 'react'
import {useAlert} from 'react-alert'
import {DataEditValueUpsert, DataEditFieldValue} from '~/globalTypes'
import {getGraphQLErrorMessage} from '~/util'
import {
    DataEditGetValues,
    DataEditGetValuesVariables,
    DataEditGetValues_dataEditValues,
} from './__types__/DataEditGetValues'
import {DataEditUpdateValues, DataEditUpdateValuesVariables} from './__types__/DataEditUpdateValues'
import {DataEditAddNewProfiles, DataEditAddNewProfilesVariables} from './__types__/DataEditAddNewProfiles'
import {DataEditDeleteProfiles, DataEditDeleteProfilesVariables} from './__types__/DataEditDeleteProfiles'
import {groupBy, orderBy} from 'lodash'
import {ReadFieldFunction} from '@apollo/client/cache/core/types/common'
import {arrayify} from '~/util/array'

export type DataEditValue = DataEditGetValues_dataEditValues

export const DATA_EDIT_GET_VALUES = gql`
    query DataEditGetValues(
        $organisationIdStr: String!
        $profileIdStr: String
        $fieldName: String
        $useProfileLabels: Boolean
        $hideHideableFields: Boolean
    ) {
        dataEditValues(
            organisationIdStr: $organisationIdStr
            profileIdStr: $profileIdStr
            fieldName: $fieldName
            useProfileLabels: $useProfileLabels
            hideHideableFields: $hideHideableFields
        ) {
            fieldOrParentCategoryIdStr
            profileIdStr
            value
            organisationIdStr
        }
    }
`

const DATA_EDIT_UPDATE_VALUES = gql`
    mutation DataEditUpdateValues($organisationIdStr: String!, $dataEditValues: [DataEditValueUpsert!]!) {
        updateDataEditValues(organisationIdStr: $organisationIdStr, dataEditValues: $dataEditValues) {
            fieldOrParentCategoryIdStr
            profileIdStr
            value
            organisationIdStr
        }
    }
`

const DATA_EDIT_ADD_NEW_PROFILES = gql`
    mutation DataEditAddNewProfiles($organisationIdStr: String!, $newProfiles: [[DataEditFieldValue!]!]!) {
        addNewProfiles(organisationIdStr: $organisationIdStr, newProfiles: $newProfiles) {
            fieldOrParentCategoryIdStr
            profileIdStr
            value
            organisationIdStr
        }
    }
`

const DATA_EDIT_DELETE_PROFILES = gql`
    mutation DataEditDeleteProfiles($organisationIdStr: String!, $profileIdStrs: [String!]!) {
        deleteProfiles(organisationIdStr: $organisationIdStr, profileIdStrs: $profileIdStrs)
    }
`

type DataEditValueCached = {
    __typename: 'DataEditValue'
    fieldOrParentCategoryIdStr: string
    profileIdStr: string
    value: string | null
}

export interface SortField {
    idStr: string
    direction?: 'asc' | 'desc'
}

export const useQueryDataEditGetValues = (
    organisationIdStr: string,
    profileIdStr?: string,
    noCache?: boolean,
    useProfileLabels?: boolean,
    hideHideableFields?: boolean,
): [
    QueryResult<DataEditGetValues, DataEditGetValuesVariables>,
    DataEditValue[],
    {
        [k: string]: DataEditValue[]
    },
    string[],
    SortField | undefined,
    (value: SortField) => void,
] => {
    const query = useQuery<DataEditGetValues, DataEditGetValuesVariables>(DATA_EDIT_GET_VALUES, {
        variables: {organisationIdStr, profileIdStr, useProfileLabels, hideHideableFields},
        ...(noCache ? {fetchPolicy: 'no-cache'} : {}),
    })
    const [dataView, setDataView] = useState<DataEditValue[]>([])
    const [dataIndexedByProfile, setDataIndexedByProfile] = useState<{
        [k: string]: DataEditValue[]
    }>({})
    const [profileIds, setProfileIds] = useState<string[]>([])
    const [sortField, setSortField] = useState<SortField | undefined>()

    useEffect(() => {
        const newDataView = (query.data?.dataEditValues || []).filter(
            (value) => value.value !== null && value.organisationIdStr === organisationIdStr,
        )
        const newDataIndexedByProfile = groupBy(newDataView, (value) => value.profileIdStr)
        const newProfileIds = [...Object.keys(newDataIndexedByProfile)]
        setDataView(newDataView)
        setDataIndexedByProfile(newDataIndexedByProfile)
        setProfileIds([
            ...profileIds.filter((profileId) => newProfileIds.includes(profileId)),
            ...newProfileIds.filter((profileId) => !profileIds.includes(profileId)),
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query])

    useEffect(() => {
        setProfileIds(
            sortField === undefined
                ? profileIds
                : orderBy(
                      profileIds,
                      (profileId) => {
                          const rawValue = dataIndexedByProfile[profileId].find(
                              (value) => value.fieldOrParentCategoryIdStr === sortField.idStr,
                          )?.value
                          return (rawValue === undefined || rawValue === '') && sortField.direction !== 'desc'
                              ? 'ZZZZZZ'
                              : rawValue === undefined || rawValue === ''
                              ? ''
                              : isNaN(parseFloat(rawValue))
                              ? rawValue.toUpperCase()
                              : rawValue.padStart(20, '0')
                      },
                      arrayify(sortField.direction),
                  ),
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortField])

    return [query, dataView, dataIndexedByProfile, profileIds, sortField, setSortField]
}

export const cachePolicyDataEditGetValues = {
    read: (existing: StoreObject[] = [], {readField}: {readField: ReadFieldFunction}) => {
        return existing.filter((e) => readField<string>('value', e) !== null)
    },
    merge: (existing: StoreObject[] = [], incoming: StoreObject[], {readField}: {readField: ReadFieldFunction}) => {
        const isSameObject = (a: StoreObject, b: StoreObject) =>
            readField<string>('fieldOrParentCategoryIdStr', a) === readField<string>('fieldOrParentCategoryIdStr', b) &&
            readField<string>('profileIdStr', a) === readField<string>('profileIdStr', b)
        return [
            ...existing.map(
                (existingObject) =>
                    incoming.find((incomingObject) => isSameObject(existingObject, incomingObject)) || existingObject,
            ),
            ...incoming.filter(
                (incomingObject) => !existing.some((existingObject) => isSameObject(existingObject, incomingObject)),
            ),
        ].filter((v) => v.value !== null)
    },
}

export const useCacheDataEditGetValues = () => {
    const client = useApolloClient()
    const read = (organisationIdStr: string) =>
        (
            client.readQuery<DataEditGetValues, DataEditGetValuesVariables>({
                query: DATA_EDIT_GET_VALUES,
                variables: {organisationIdStr},
            })?.dataEditValues || []
        ).filter(({value}) => value !== null)

    const write = (
        organisationIdStr: string,
        values: DataEditValueCached[] | ((currentData: DataEditValueCached[]) => DataEditValueCached[]),
    ) => {
        const data = (typeof values === 'function' ? values(read(organisationIdStr)) : values).map((newValue) => ({
            ...newValue,
            id: `DataEditValue:{"fieldOrParentCategoryIdStr":${newValue.fieldOrParentCategoryIdStr},"profileIdStr":${newValue.profileIdStr}}`,
        }))
        client.writeQuery({
            query: DATA_EDIT_GET_VALUES,
            data: {
                dataEditValues: data,
            },
            variables: {organisationIdStr},
        })
    }

    const remove = (
        organisationIdStr: string,
        criteria?: {profileIdStrs?: string[]; fieldOrParentCategoryIdStrs?: string[]},
    ) =>
        write(organisationIdStr, (values) =>
            values
                .filter((value) => !criteria?.profileIdStrs || criteria.profileIdStrs.includes(value.profileIdStr))
                .filter(
                    (value) =>
                        !criteria?.fieldOrParentCategoryIdStrs ||
                        criteria.fieldOrParentCategoryIdStrs.includes(value.fieldOrParentCategoryIdStr),
                )
                .map((value) => ({...value, value: null})),
        )

    return [
        {
            read,
            write,
            remove,
        },
    ]
}

export const useMutationDataEditUpdateValues = (
    organisationIdStr: string,
): [
    MutationTuple<DataEditUpdateValues, DataEditUpdateValuesVariables>,
    (variables: {
        dataEditValues: DataEditValueUpsert[]
    }) => Promise<FetchResult<DataEditUpdateValues, Record<string, any>, Record<string, any>>>,
] => {
    const alert = useAlert()
    const [dataEditValuesCache] = useCacheDataEditGetValues()
    const mutation = useMutation<DataEditUpdateValues, DataEditUpdateValuesVariables>(DATA_EDIT_UPDATE_VALUES, {})

    const callMutation = async (variables: {dataEditValues: DataEditValueUpsert[]}) => {
        try {
            const result = await mutation[0]({variables: {organisationIdStr, ...variables}})
            await dataEditValuesCache.write(organisationIdStr, result!.data!.updateDataEditValues!)
            return result
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}

export const useMutationDataEditAddNewProfiles = (
    organisationIdStr: string,
): [
    MutationTuple<DataEditAddNewProfiles, DataEditAddNewProfilesVariables>,
    (variables: {
        newProfiles: DataEditFieldValue[][]
    }) => Promise<FetchResult<DataEditAddNewProfiles, Record<string, any>, Record<string, any>>>,
] => {
    const alert = useAlert()
    const [dataEditValuesCache] = useCacheDataEditGetValues()
    const mutation = useMutation<DataEditAddNewProfiles, DataEditAddNewProfilesVariables>(
        DATA_EDIT_ADD_NEW_PROFILES,
        {},
    )
    const callMutation = async (variables: {newProfiles: DataEditFieldValue[][]}) => {
        try {
            const result = await mutation[0]({variables: {organisationIdStr, ...variables}})
            await dataEditValuesCache.write(organisationIdStr, result!.data!.addNewProfiles!)
            return result
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}

export const useMutationDataEditDeleteProfiles = (
    organisationIdStr: string,
): [
    MutationTuple<DataEditDeleteProfiles, DataEditDeleteProfilesVariables>,
    (variables: {
        profileIdStrs: string[]
    }) => Promise<FetchResult<DataEditDeleteProfiles, Record<string, any>, Record<string, any>>>,
] => {
    const alert = useAlert()
    const [dataEditValuesCache] = useCacheDataEditGetValues()
    const mutation = useMutation<DataEditDeleteProfiles, DataEditDeleteProfilesVariables>(DATA_EDIT_DELETE_PROFILES, {})
    const callMutation = async (variables: {profileIdStrs: string[]}) => {
        try {
            const result = await mutation[0]({variables: {organisationIdStr, ...variables}})
            await dataEditValuesCache.remove(organisationIdStr, {profileIdStrs: variables.profileIdStrs})
            return result
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}
